import React, { useEffect, useState } from "react";
import { fmtInt, parseIntNL } from "@/lib/api";

/**
 * MoneyInput — plain text input for whole-euro amounts with NL thousand separators.
 * Focus reveals raw digits, blur reformats. Emits numeric values via onCommit.
 *
 * Props
 *  - value:        current numeric value (number | "" | null)
 *  - onCommit:     (number) => void, called on blur or Enter with the rounded integer
 *  - testid:       optional data-testid
 *  - className:    input className (defaults to a standard wizard style)
 *  - placeholder:  input placeholder (defaults to "0")
 *  - allowEmpty:   when true, empty input commits null instead of 0
 */
export function MoneyInput({ value, onCommit, testid, className, placeholder = "0", allowEmpty = false, readOnly = false }) {
  const roundInt = (n) => Math.round(Number(n) || 0);
  const isEmpty = (v) => v === "" || v === null || v === undefined;
  const format = (n) => (isEmpty(n) ? "" : fmtInt(roundInt(n)));

  const [val, setVal] = useState(format(value));

  // Re-sync when the parent-provided value changes and the input isn't focused.
  useEffect(() => {
    if (document.activeElement && document.activeElement.getAttribute("data-testid") === testid) return;
    setVal(format(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const cls = className || "w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary";

  return (
    <input
      type="text"
      inputMode="numeric"
      value={val}
      placeholder={placeholder}
      data-testid={testid}
      readOnly={readOnly}
      onFocus={(e) => {
        if (readOnly) return;
        // Reveal raw digits so keystrokes edit natural text.
        setVal(isEmpty(value) ? "" : String(roundInt(value)));
        requestAnimationFrame(() => e.target.select());
      }}
      onChange={(e) => {
        if (readOnly) return;
        const cleaned = e.target.value.replace(/[^0-9-]/g, "").replace(/(?!^)-/g, "");
        setVal(cleaned);
      }}
      onKeyDown={(e) => {
        if (readOnly) return;
        if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
        else if (e.key === "Escape") { e.preventDefault(); setVal(format(value)); e.currentTarget.blur(); }
      }}
      onBlur={() => {
        if (readOnly) return;
        if (allowEmpty && val.trim() === "") {
          setVal("");
          if (value !== null && value !== "" && value !== undefined) onCommit(null);
          return;
        }
        const n = parseIntNL(val);
        const rounded = roundInt(Number.isFinite(n) ? n : 0);
        setVal(format(rounded));
        // Only fire onCommit when the numeric value actually changed. Prevents spurious
        // PUTs when a user tabs through unchanged cells.
        if (rounded !== roundInt(value)) onCommit(rounded);
      }}
      className={cls}
    />
  );
}
